//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { VariantsTable } from '@campaigns/components/shared/product-details-modal/variants/VariantsTable';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { TertiaryButton } from '@components/atoms/button/Button';

export const DEFAULT_VARIATION = {
  fairMarketValue: undefined,
  imgUrl: undefined,
  logisticsRef: undefined,
  quantity: undefined,
  variantName: undefined,
};

interface VariantsTabProps {
  isViewOnly: boolean;
}

export const VariantsTab = ({ isViewOnly }: VariantsTabProps) => {
  const { control, getValues, setValue } =
    useFormContext<EditableBVSamplingProduct>();
  const { isImageForAllVariants, removeVariantImage } = useProductModal();
  const { fields } = useFieldArray({
    control,
    name: 'variations',
  });

  const hasMultipleVariants = fields.length > 1;

  const handleAddVariation = () => {
    const currentValues = getValues();
    const mainImgUrl = currentValues.imgUrl;

    // With "Use for all variants" on, new variants inherit the main image so the user needn't
    // set one manually (which would toggle the flag off).
    const newVariation =
      isImageForAllVariants && mainImgUrl
        ? { ...DEFAULT_VARIATION, imgUrl: mainImgUrl }
        : { ...DEFAULT_VARIATION };

    setValue('variations', [...currentValues.variations, newVariation], {
      shouldValidate: true,
    });
  };

  const handleRemoveVariation = (index: number) => {
    const currentValues = getValues();
    removeVariantImage(index);
    setValue(
      'variations',
      [
        ...currentValues.variations.slice(0, index),
        ...currentValues.variations.slice(index + 1),
      ],
      {
        shouldValidate: true,
      },
    );
  };

  return (
    <Container>
      {hasMultipleVariants && (
        <VariantsTable
          fields={fields}
          isViewOnly={isViewOnly}
          onRemoveVariant={handleRemoveVariation}
        />
      )}
      {!isViewOnly && (
        <StyledTertiaryButton
          onClick={handleAddVariation}
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_ADD_VARIATION
          }
        >
          Add Variation
        </StyledTertiaryButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const StyledTertiaryButton = styled(TertiaryButton)`
  align-self: flex-start;
`;
